import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Layers,
  Search,
  X,
  ArrowUpDown,
  Users,
  ChevronRight,
  Download,
  Upload,
  Calendar,
  FolderKanban,
} from 'lucide-react';
import { toast } from 'sonner';
import { statusConfig, ragPriority } from '../lib/portfolioConstants';
import RagBadge from '../components/RagBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProgramsPage = () => {
  const [programs, setPrograms] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rag');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [programProjects, setProgramProjects] = useState([]);
  const [nestedProject, setNestedProject] = useState(null);
  const [showNestedModal, setShowNestedModal] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const exportCSV = async () => {
    try {
      const res = await axios.get(`${API}/executive/programs/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'programs.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed', e); toast.error('Export failed'); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!globalThis.confirm(`This will replace ALL programs with data from "${file.name}". Continue?`)) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/executive/programs/import`, form);
      toast.success(`Imported ${res.data.imported} programs`);
      fetchData();
    } catch (e) { console.error('Import failed', e); toast.error('Import failed'); }
    finally { setImporting(false); }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [progRes, teamRes] = await Promise.all([
        axios.get(`${API}/executive/programs`),
        axios.get(`${API}/teams/`)
      ]);
      setPrograms(progRes.data || []);
      setTeams(teamRes.data || []);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  };

  const openProgram = async (program) => {
    setSelectedProgram(program);
    setShowModal(true);
    try {
      const res = await axios.get(`${API}/executive/programs/${program.id}`);
      setProgramProjects(res.data.projects || []);
    } catch (e) { console.error('Failed to load program projects:', e); setProgramProjects([]); }
  };

  const filtered = useMemo(() => {
    let result = [...programs];
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.name?.toLowerCase().includes(q) || p.key?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q) || p.owner?.toLowerCase().includes(q) ||
        p.current_status_text?.toLowerCase().includes(q) || p.risk_summary?.toLowerCase().includes(q)
      );
    }
    if (selectedTeamFilter !== 'all') {
      result = result.filter(p => p.team_ids?.includes(selectedTeamFilter));
    }
    if (sortBy === 'rag') result.sort((a, b) => (ragPriority[a.status] ?? 5) - (ragPriority[b.status] ?? 5));
    else if (sortBy === 'progress') result.sort((a, b) => a.progress_percent - b.progress_percent);
    else if (sortBy === 'name') result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [programs, searchQuery, selectedTeamFilter, sortBy]);

  if (isLoading) {
    return (
      <div className="p-8 space-y-4 animate-pulse" data-testid="programs-loading">
        <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded-lg"></div>
        {[1,2,3].map(i => <div key={i} className="h-48 bg-slate-200 dark:bg-slate-700 rounded-xl"></div>)}
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="programs-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#00C26D]/10 dark:bg-[#00C26D]/15 rounded-xl flex items-center justify-center">
            <Layers className="w-6 h-6 text-[#00995A] dark:text-[#00D97E]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Programs</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{filtered.length} of {programs.length} programs</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D] hover:text-[#00995A] transition-all">
            <Download className="w-3 h-3" /> Export CSV
          </button>
          <label className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all ${importing ? 'opacity-50 pointer-events-none' : ''} bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D] hover:text-[#00995A]`}>
            <Upload className="w-3 h-3" /> {importing ? 'Importing…' : 'Import CSV'}
            <input type="file" accept=".csv" className="hidden" onChange={importCSV} disabled={importing} />
          </label>
          {['rag', 'progress', 'name'].map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${sortBy === s ? 'bg-[#00C26D] text-[#1A1A1A]' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700'}`}
              data-testid={`sort-${s}`}
            >
              <ArrowUpDown className="w-3 h-3 inline mr-1" />{s === 'rag' ? 'RAG Status' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search programs by name, key, owner, status, or risk..."
          className="input-glass w-full pl-10 pr-10"
          data-testid="search-programs"
        />
        {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-slate-400" /></button>}
      </div>

      {/* Team Pills */}
      <div className="flex flex-wrap gap-2" data-testid="team-pills">
        <button onClick={() => setSelectedTeamFilter('all')}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter === 'all' ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
        >All Teams</button>
        {teams.map(t => (
          <button key={t.id} onClick={() => setSelectedTeamFilter(selectedTeamFilter === t.id ? 'all' : t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${selectedTeamFilter === t.id ? 'bg-[#00C26D] text-[#1A1A1A] shadow-md shadow-[#00C26D]/20' : 'bg-slate-100 dark:bg-slate-800/60 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-[#00C26D]/50'}`}
          >{t.name}</button>
        ))}
      </div>

      {/* Program Cards - 4-Blocker Format */}
      <div className="space-y-4">
        {filtered.map(program => {
          const sc = statusConfig[program.status] || statusConfig.green;
          const StatusIcon = sc.icon;
          return (
            <button key={program.id} type="button"
              className={`w-full text-left glass-card rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all border-l-4 ${sc.border}`}
              onClick={() => openProgram(program)}
              data-testid={`program-card-${program.key}`}
            >
              {/* Card Header */}
              <div className="px-6 py-4 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${sc.bgLight} ${sc.textColor} flex items-center gap-1`}>
                    <StatusIcon className="w-3 h-3" />{sc.label}
                  </span>
                  <span className="text-xs text-slate-400 font-mono">{program.key}</span>
                  <h3 className="font-semibold text-slate-900 dark:text-white">{program.name}</h3>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                  <span>{program.owner}</span>
                  <span className="font-mono">{program.progress_percent}%</span>
                  <div className="w-20 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full ${sc.color} rounded-full`} style={{ width: `${program.progress_percent}%` }} />
                  </div>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
              
              {/* 4-Blocker Grid */}
              <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-slate-100 dark:divide-slate-800">
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#00995A] dark:text-[#00D97E] mb-1.5">IT Product Manager</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-3">{program.technical_project_manager || '—'}</p>
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">RAG Status</p>
                  <RagBadge rag={program.rag} />
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400 mb-1.5">Tier</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300">{program.tier ? `Tier ${program.tier}` : '—'}</p>
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600 dark:text-purple-400 mb-1.5">IT POV</p>
                  <p className="text-xs text-slate-700 dark:text-slate-300 line-clamp-5">{program.it_pov || '—'}</p>
                </div>
              </div>
              
              {/* Footer - Team Tags */}
              <div className="px-6 py-2 bg-slate-50/50 dark:bg-slate-800/30 flex items-center gap-2 flex-wrap">
                <Users className="w-3 h-3 text-slate-400" />
                {program.team_names?.map((tn) => (
                  <span key={tn} className="text-[10px] px-2 py-0.5 bg-white dark:bg-slate-800 rounded-full text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700">{tn}</span>
                ))}
                <span className="text-[10px] text-slate-400 ml-auto">{program.project_count} projects | {program.budget}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Program Detail Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          {selectedProgram && (() => {
            const sc = statusConfig[selectedProgram.status] || statusConfig.green;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1.5 text-sm font-bold rounded ${sc.bgLight} ${sc.textColor}`}>{sc.label}</span>
                    {selectedProgram.tier && (
                      <span className="px-3 py-1 text-xs font-bold rounded border bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600">
                        T{selectedProgram.tier}
                      </span>
                    )}
                    <span className="text-sm text-slate-400 font-mono">{selectedProgram.key}</span>
                    <span className="text-base font-semibold text-slate-900 dark:text-white">{selectedProgram.name}</span>
                  </DialogTitle>
                </DialogHeader>

                {/* Meta row — IT Product Manager as text */}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500 dark:text-slate-400 mt-2">
                  <span>IT Product Manager: <span className="text-slate-700 dark:text-slate-300">{selectedProgram.technical_project_manager || '—'}</span></span>
                </div>

                {/* Date tiles */}
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-4 rounded-xl border border-[#00C26D]/30 dark:border-[#00C26D]/30 bg-[#00C26D]/5 dark:bg-[#00C26D]/10">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#00995A] dark:text-[#00D97E] mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Start Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{selectedProgram.start_date || '—'}</p>
                  </div>
                  <div className="p-4 rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-orange-700 dark:text-orange-400 mb-2 flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" />Go-Live Date
                    </h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{selectedProgram.go_live_date || '—'}</p>
                  </div>
                </div>

                {/* Milestones/Risk (IT POV) */}
                <div className="mt-4 p-4 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 min-h-[80px]">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400 mb-2">Milestones/Risk</h4>
                  <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedProgram.it_pov || '—'}</p>
                </div>

                {/* Status */}
                {selectedProgram.current_status_text && (
                  <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">Status</h4>
                    <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{selectedProgram.current_status_text}</p>
                  </div>
                )}

                {/* Projects in this Program (clickable) */}
                {programProjects.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                      <FolderKanban className="w-3.5 h-3.5" />Projects in this Program ({programProjects.length})
                    </h4>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {programProjects.map(proj => {
                        const psc = statusConfig[proj.status] || statusConfig.green;
                        return (
                          <button key={proj.id} type="button"
                            className="w-full flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 hover:bg-[#00C26D]/5 dark:hover:bg-[#00C26D]/5 transition-colors text-left"
                            onClick={() => { setNestedProject(proj); setShowNestedModal(true); }}
                          >
                            <span className={`w-2 h-2 rounded-full shrink-0 ${psc.color}`} />
                            <span className="text-xs font-mono text-slate-400 shrink-0">{proj.key}</span>
                            <span className="text-sm text-slate-700 dark:text-slate-300 flex-1 truncate">{proj.name}</span>
                            <span className="text-xs text-slate-500 shrink-0">{proj.progress_percent}%</span>
                            <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Contributing Teams — bottom */}
                {selectedProgram.rollup_team_contributing && (
                  <div className="mt-4 p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5" />Contributing Teams
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProgram.rollup_team_contributing.replaceAll(';', ',').split(',').map(t => t.trim()).filter(Boolean).map(team => (
                        <span key={team} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00C26D]/10 dark:bg-[#00C26D]/15 text-[#00995A] dark:text-[#00D97E] border border-[#00C26D]/30 dark:border-[#00C26D]/30">
                          {team}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Nested Project Detail Modal */}
      <Dialog open={showNestedModal} onOpenChange={setShowNestedModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {nestedProject && (() => {
            const nsc = statusConfig[nestedProject.status] || statusConfig.green;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3 flex-wrap">
                    <span className={`px-3 py-1.5 text-sm font-bold rounded ${nsc.bgLight} ${nsc.textColor}`}>{nsc.label}</span>
                    <span className="text-sm text-slate-400 font-mono">{nestedProject.key}</span>
                    <span className="text-base font-semibold text-slate-900 dark:text-white">{nestedProject.name}</span>
                  </DialogTitle>
                </DialogHeader>
                <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                  {nestedProject.team_name && <span className="mr-6">Team: <span className="text-slate-700 dark:text-slate-300">{nestedProject.team_name}</span></span>}
                  {nestedProject.project_manager && <span>IT TPM: <span className="text-slate-700 dark:text-slate-300">{nestedProject.project_manager}</span></span>}
                </div>
                <div className="mt-3 text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                  <span className="text-slate-500">Progress:</span>
                  <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className={`h-full ${nsc.color} rounded-full`} style={{ width: `${nestedProject.progress_percent || 0}%` }} />
                  </div>
                  <span className="font-mono text-xs">{nestedProject.progress_percent || 0}%</span>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProgramsPage;
